/** Schemas Zod e tipos da API de treino e corrida (7.8). */
import { z } from 'zod';
import type { EquipmentKind, SessionStatus } from '../types';
import type { CardioPhaseDef, FlatCardioStep } from '../rules/workout';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const kg = z.number().min(0).max(999);

// ---------------------------------------------------------------- requisições

export const workoutDayQuery = z.object({ date: isoDate.optional() });

export const startSessionBody = z.object({
  clientId: z.string().uuid().optional(),
  date: isoDate,
  templateCode: z.string().length(1),
});

export const patchSessionBody = z.object({
  status: z.enum(['in_progress', 'done', 'abandoned']).optional(),
  note: z.string().max(2000).nullable().optional(),
});

export const saveSetBody = z.object({
  clientId: z.string().uuid().optional(),
  exerciseId: z.string().uuid(),
  setNumber: z.number().int().min(1).max(20),
  weightKg: kg.nullable().optional(),
  reps: z.number().int().min(0).max(500).nullable().optional(),
  durationS: z.number().int().min(0).max(24 * 3600).nullable().optional(),
});

export const patchSetBody = z.object({
  weightKg: kg.nullable().optional(),
  reps: z.number().int().min(0).max(500).nullable().optional(),
  durationS: z.number().int().min(0).max(24 * 3600).nullable().optional(),
});

export const painBody = z.object({ exerciseId: z.string().uuid(), note: z.string().max(500).nullable().optional() });

export const gameFeedbackBody = z.object({ date: isoDate, worse: z.boolean().default(true) });

export const machineBody = z.object({
  settings: z.string().max(200).nullable().optional(),
  incrementKg: z.number().min(0.5).max(20).nullable().optional(),
  lastWeightKg: kg.nullable().optional(),
});

export const saveRunBody = z.object({
  clientId: z.string().uuid().optional(),
  workoutSessionId: z.string().uuid().nullable().optional(),
  completedRounds: z.number().int().min(0).max(100),
  totalRounds: z.number().int().min(1).max(100),
  durationS: z.number().int().min(0).max(6 * 3600),
});

export const saveRunTestBody = z.object({
  date: isoDate,
  durationS: z.number().int().min(60).max(3 * 3600),
});

export type StartSessionBody = z.infer<typeof startSessionBody>;
export type SaveSetBody = z.infer<typeof saveSetBody>;
export type MachineBody = z.infer<typeof machineBody>;
export type SaveRunBody = z.infer<typeof saveRunBody>;
export type SaveRunTestBody = z.infer<typeof saveRunTestBody>;

// ---------------------------------------------------------------- respostas

export type WorkoutSetLog = {
  id: string;
  exerciseId: string;
  setNumber: number;
  weightKg: number | null;
  reps: number | null;
  durationS: number | null;
  completedAt: string;
};

export type WorkoutScreenAlert = {
  code: string;
  tone: 'gym' | 'warn' | 'good' | 'info';
  kanji: string;
  title: string;
  body: string;
};

export type WorkoutCardio = {
  program: string;
  week: number;
  phase: Pick<CardioPhaseDef, 'id' | 'title' | 'detail' | 'speedNote' | 'segments'>;
  steps: FlatCardioStep[];
  totalRounds: number;
  totalS: number;
};

/** Um exercício dentro de um item (2 no bi-set). */
export type WorkoutDayExercise = {
  id: string;
  code: string;
  name: string;
  equipment: EquipmentKind;
  atGym: boolean;
  cue: string | null;
  /** com carga (máquina) */
  weighted: boolean;
  target: { reps: number | null; seconds: number | null };
  needsMachineSetup: boolean;
  machine: { settings: string | null; incrementKg: number | null; lastWeightKg: number | null } | null;
  incrementKg: number;
  last: { date: string; weightKg: number | null; sets: { setNumber: number; weightKg: number | null; reps: number | null }[] } | null;
  suggestedKg: number | null;
  progressUp: { title: string; body: string } | null;
  painLastTime: boolean;
  painReminder: string | null;
  /** séries já registradas hoje */
  logged: WorkoutSetLog[];
  painToday: { id: string } | null;
};

export type WorkoutItemStatus = 'pending' | 'in_progress' | 'done' | 'skipped';

export type WorkoutDayItem = {
  itemId: string;
  sort: number;
  label: string;
  block: string;
  name: string;
  cue: string | null;
  unilateral: boolean;
  sets: number | null;
  baseSets: number | null;
  reps: number | null;
  repsText: string;
  restS: number | null;
  atGym: boolean;
  /** weighted: tabela kg/reps · reps: só repetições · timed: séries por tempo/voltas · single: um bloco só · cardio: timer */
  kind: 'weighted' | 'reps' | 'timed' | 'single' | 'cardio';
  isBiset: boolean;
  exercises: WorkoutDayExercise[];
  cardio: WorkoutCardio | null;
  durationS: number | null;
  status: WorkoutItemStatus;
};

export type WorkoutDay = {
  date: string;
  weekdayLabel: string; // "TERÇA"
  planWeek: number;
  isAdaptation: boolean;
  isDeload: boolean;
  isGameDay: boolean;
  isRunDay: boolean;
  isRunTestDay: boolean;
  runProgramWeek: number;
  gymName: string | null;
  trainingTime: string;
  gameTime: string;
  template: { id: string; code: string; name: string; durationMin: number; note: string | null };
  exerciseCount: number;
  doneCount: number;
  session: { id: string; status: SessionStatus; startedAt: string; finishedAt: string | null; note: string | null } | null;
  items: WorkoutDayItem[];
  /** avisos de tela (screen:treino) */
  alerts: WorkoutScreenAlert[];
  gameFeedback: { worse: boolean } | null;
};

export type RunCurrent = {
  date: string;
  isThursday: boolean;
  programWeek: number;
  planWeek: number;
  isTestDay: boolean;
  phase: WorkoutCardio;
  afterRun: { code: string; name: string; cue: string | null; repsText: string }[];
  lastTest: { date: string; durationS: number; planWeek: number } | null;
  todayTest: { id: string; durationS: number } | null;
  todayRun: { id: string; completed: boolean; completedRounds: number; totalRounds: number; programWeek: number } | null;
  workoutSessionId: string | null;
  alerts: WorkoutScreenAlert[];
};

export type SaveRunResult = {
  run: { id: string; completed: boolean; programWeek: number; completedRounds: number; totalRounds: number };
  programWeek: number;
  alert: WorkoutScreenAlert | null;
};

export type MachineSettingsDto = {
  exerciseId: string;
  settings: string | null;
  incrementKg: number | null;
  lastWeightKg: number | null;
  defaultIncrementKg: number | null;
};
